/**
 * Utility to get the current organization ID
 * This is used to scope queries for organization-specific data
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/**
 * Get the current organization ID from Clerk via the database mapping
 * This assumes the organization ID is passed from components using useOrganization hook
 */
export async function getCurrentOrganizationId(): Promise<string | null> {
  try {
    // Get the current user's Clerk ID from localStorage or session
    const clerkUserId = localStorage.getItem('clerk_user_id');
    
    if (!clerkUserId) {
      return null;
    }

    // Get the organization ID from the database mapping
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('clerk_org_mappings')
      .select('clerk_org_id')
      .eq('clerk_user_id', clerkUserId)
      .single();

    if (error || !data) {
      return null;
    }

    return data.clerk_org_id;
  } catch (error) {
    logger.error('Error getting organization ID:', error);
    return null;
  }
}

/**
 * Store the current organization ID in session storage
 * This should be called when the organization context changes
 */
export function setCurrentOrganizationId(orgId: string | null): void {
  if (orgId) {
    sessionStorage.setItem('current_org_id', orgId);
  } else {
    sessionStorage.removeItem('current_org_id');
  }
}

/**
 * Get the cached organization ID from session storage
 * This is faster than querying the database
 */
export function getCachedOrganizationId(): string | null {
  return sessionStorage.getItem('current_org_id');
}
